import React, { useEffect, useState, useCallback } from 'react';
import friendlyError from '../../../utils/friendlyError';
import styled from 'styled-components';
import { FaTrash, FaPlus, FaCheck, FaSpinner } from 'react-icons/fa';
import {
  TechInput,
  TechSelect,
  TechButton,
  TechButtonGhost,
  borderSoft,
  panelDeep,
  mono,
} from '../../../styles/techComponents';
import {
  listElections,
  createElection,
  deleteElection,
} from '../../../services/platformService';

const Field = styled.div`
  margin-bottom: 1.1rem;

  > label {
    display: block;
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin-bottom: 0.4rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.6rem 0.7rem;

  small {
    display: block;
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin-bottom: 0.25rem;
  }
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.6rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 5px;
  font-size: 0.85rem;
  color: var(--text-secondary);
  align-self: end;
  cursor: pointer;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 0.9rem;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  background: ${panelDeep};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;

  th, td {
    padding: 0.55rem 0.7rem;
    text-align: left;
    border-bottom: 1px solid ${borderSoft};
    color: var(--text-secondary);
    vertical-align: middle;
  }

  th {
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-disabled);
    background: rgba(0, 180, 216, 0.04);
  }

  td.num { text-align: right; font-variant-numeric: tabular-nums; }

  a { color: var(--btn-primary); text-decoration: none; }
  a:hover { text-decoration: underline; }

  tr:last-child td { border-bottom: 0; }
`;

const Empty = styled.div`
  padding: 1rem;
  text-align: center;
  font-size: 0.85rem;
  color: var(--text-disabled);
  font-style: italic;
`;

const ErrorBox = styled.div`
  padding: 0.6rem 0.8rem;
  border: 1px solid rgba(239, 68, 68, 0.4);
  background: rgba(239, 68, 68, 0.08);
  color: #fca5a5;
  border-radius: 5px;
  font-size: 0.82rem;
  margin-bottom: 1rem;
`;

const Hint = styled.p`
  font-size: 0.78rem;
  color: var(--text-disabled);
  margin: 0.4rem 0 0;
`;

const FORCE_SUGGESTIONS = [
  'La Libertad Avanza',
  'Fuerza Patria',
  'Unión por la Patria',
  'Juntos por el Cambio',
  'PRO',
  'UCR',
  'Primero San Nicolás',
  'HECHOS',
  'Otros',
];

const EMPTY_FORM = {
  election_date: '2025-10-26',
  level: 'nacional',
  district: 'San Nicolás',
  force: '',
  percent: '',
  votes: '',
  seats_won: '',
  source: '',
  source_url: '',
  notes: '',
  is_official: true,
};

const ElectionsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listElections();
      setItems(rows || []);
      setError(null);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onChange = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.election_date || !form.level || !form.force.trim()) {
      setError('Fecha, nivel y fuerza son obligatorios.');
      return;
    }
    setSubmitting(true);
    try {
      await createElection({
        election_date: form.election_date,
        level: form.level,
        district: form.district || null,
        force: form.force.trim(),
        percent: form.percent === '' ? null : Number(form.percent),
        votes: form.votes === '' ? null : Number(form.votes),
        seats_won: form.seats_won === '' ? null : Number(form.seats_won),
        source: form.source || null,
        source_url: form.source_url || null,
        notes: form.notes || null,
        is_official: !!form.is_official,
      });
      setForm((prev) => ({
        ...EMPTY_FORM,
        election_date: prev.election_date,
        level: prev.level,
        district: prev.district,
        is_official: prev.is_official,
        source: prev.source,
        source_url: prev.source_url,
      }));
      setError(null);
      await load();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id, label) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`¿Eliminar el resultado de ${label}?`)) return;
    try {
      await deleteElection(id);
      await load();
    } catch (err) {
      setError(friendlyError(err));
    }
  };

  const sorted = [...items].sort((a, b) => {
    const d = String(b.election_date).localeCompare(String(a.election_date));
    if (d !== 0) return d;
    return (Number(b.percent) || 0) - (Number(a.percent) || 0);
  });

  return (
    <>
      {error && <ErrorBox>{error}</ErrorBox>}

      <Field as="form" onSubmit={onSubmit}>
        <label>Cargar resultado</label>
        <Grid>
          <div>
            <small>Fecha *</small>
            <TechInput
              type="date"
              value={form.election_date}
              onChange={onChange('election_date')}
              required
            />
          </div>
          <div>
            <small>Nivel *</small>
            <TechSelect value={form.level} onChange={onChange('level')} required>
              <option value="municipal">Municipal</option>
              <option value="provincial">Provincial</option>
              <option value="nacional">Nacional</option>
            </TechSelect>
          </div>
          <div>
            <small>Distrito</small>
            <TechInput
              value={form.district}
              onChange={onChange('district')}
              placeholder="San Nicolás"
            />
          </div>
          <div>
            <small>Fuerza *</small>
            <TechInput
              list="elections-force-list"
              value={form.force}
              onChange={onChange('force')}
              placeholder="ej: La Libertad Avanza"
              required
            />
            <datalist id="elections-force-list">
              {FORCE_SUGGESTIONS.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </div>
          <div>
            <small>%</small>
            <TechInput
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={form.percent}
              onChange={onChange('percent')}
              placeholder="42.2"
            />
          </div>
          <div>
            <small>Votos</small>
            <TechInput
              type="number"
              min="0"
              value={form.votes}
              onChange={onChange('votes')}
            />
          </div>
          <div>
            <small>Bancas ganadas</small>
            <TechInput
              type="number"
              min="0"
              value={form.seats_won}
              onChange={onChange('seats_won')}
            />
          </div>
          <div>
            <small>Fuente</small>
            <TechInput
              value={form.source}
              onChange={onChange('source')}
              placeholder="Junta Electoral BA"
            />
          </div>
          <div>
            <small>URL fuente</small>
            <TechInput
              type="url"
              value={form.source_url}
              onChange={onChange('source_url')}
              placeholder="https://…"
            />
          </div>
          <div>
            <small>Notas</small>
            <TechInput
              value={form.notes}
              onChange={onChange('notes')}
            />
          </div>
          <CheckRow>
            <input
              type="checkbox"
              checked={!!form.is_official}
              onChange={onChange('is_official')}
            />
            Resultado oficial
          </CheckRow>
        </Grid>
        <Actions>
          <TechButtonGhost
            type="button"
            onClick={() => setForm(EMPTY_FORM)}
            disabled={submitting}
          >
            Limpiar
          </TechButtonGhost>
          <TechButton type="submit" disabled={submitting}>
            {submitting ? <FaSpinner /> : <FaPlus />} Agregar
          </TechButton>
        </Actions>
        <Hint>
          La carga manual queda marcada como oficial por defecto. La fuerza acepta texto libre;
          las sugerencias del autocompletado usan los nombres con color asignado en el gráfico.
        </Hint>
      </Field>

      <Field>
        <label>Resultados cargados · {items.length}</label>
        {loading && items.length === 0 ? (
          <Empty>Cargando…</Empty>
        ) : items.length === 0 ? (
          <Empty>Sin resultados todavía. Cargá uno arriba.</Empty>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Nivel</th>
                  <th>Distrito</th>
                  <th>Fuerza</th>
                  <th className="num">%</th>
                  <th className="num">Votos</th>
                  <th className="num">Bancas</th>
                  <th>Oficial</th>
                  <th>Fuente</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.id}>
                    <td>{r.election_date}</td>
                    <td>{r.level}</td>
                    <td>{r.district || '—'}</td>
                    <td>{r.force}</td>
                    <td className="num">{r.percent != null ? Number(r.percent).toFixed(2) : '—'}</td>
                    <td className="num">{r.votes != null ? Number(r.votes).toLocaleString('es-AR') : '—'}</td>
                    <td className="num">{r.seats_won ?? '—'}</td>
                    <td>{r.is_official ? <FaCheck /> : '—'}</td>
                    <td>
                      {r.source_url ? (
                        <a href={r.source_url} target="_blank" rel="noreferrer">
                          {r.source || 'link'}
                        </a>
                      ) : (
                        r.source || '—'
                      )}
                    </td>
                    <td>
                      <TechButtonGhost
                        type="button"
                        onClick={() => onDelete(r.id, `${r.force} (${r.election_date})`)}
                        title="Eliminar"
                        style={{ padding: '0.3rem 0.55rem' }}
                      >
                        <FaTrash />
                      </TechButtonGhost>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Field>
    </>
  );
};

export default ElectionsTab;
